/**
 * features/hr/StaffMigrationImport.tsx
 * ------------------------------------------------------------------
 * Go-live bulk importer for staff opening balances. One row per
 * employee (matched on employee number) sets up pay + the opening
 * figures the rest of the system derives from:
 *   - basic salary, gratuity rate / eligibility
 *   - opening basic paid this contract (gratuity base at go-live)
 *   - opening leave entitlement + balance per type (annual, sick)
 *
 * Writes merge into any existing payroll profile / leave allocation,
 * so a partly-filled row never wipes data that isn't in the sheet.
 * Built on the shared CSV scaffold (lib/hrImport).
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import { Upload, Download, CheckCircle2, Loader2, X, Users } from 'lucide-react';
import { AccentButton } from '@/components/ui/glass';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import { staffFullName, type HrStaff } from '@/lib/hr';
import { round2, type HrPayrollProfile, type HrLeaveAllocation } from '@/lib/hrPayroll';
import {
  parseCsv, makeColFinder, cell, numOr, boolOf, hasValue, downloadCsv,
} from '@/lib/hrImport';

interface ParsedRow {
  staff: HrStaff;
  basic?: number;
  gratuityRate?: number;
  gratuityEligible?: boolean;
  openingBasic?: number;
  annualEnt?: number;
  annualOpen?: number;
  sickEnt?: number;
  sickOpen?: number;
}
interface Outcome {
  rows: ParsedRow[];
  unmatched: string[];
  errors: string[];
}

const TEMPLATE =
  'EmployeeNo,BasicSalary,GratuityRatePct,GratuityEligible,OpeningBasicPaidThisContract,AnnualEntitlement,AnnualOpening,SickEntitlement,SickOpening\n' +
  'E001,12000,25,yes,240000,20,5,15,0\n' +
  'E002,9000,0,no,0,20,0,15,-3';

export default function StaffMigrationImport({
  staff, profiles, allocations, onClose,
}: {
  staff: HrStaff[];
  profiles: HrPayrollProfile[];
  allocations: HrLeaveAllocation[];
  onClose: () => void;
}) {
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<number | null>(null);

  const staffByEmp = useMemo(() => {
    const m = new Map<string, HrStaff>();
    for (const s of staff) if (s.employeeNo) m.set(s.employeeNo.trim().toLowerCase(), s);
    return m;
  }, [staff]);

  function parse(src: string) {
    setDone(null);
    const p = parseCsv(src);
    if (p.error) { setOutcome({ rows: [], unmatched: [], errors: [p.error] }); return; }
    const find = makeColFinder(p.headers);
    const iEmp = find(['employeeno', 'empno', 'staffno', 'employee', 'staff', 'id']);
    if (iEmp < 0) {
      setOutcome({ rows: [], unmatched: [], errors: ['The file needs an employee-number column (e.g. "EmployeeNo").'] });
      return;
    }
    const iBasic = find(['basicsalary', 'basic', 'salary']);
    const iRate = find(['gratuityratepct', 'gratuityrate', 'rate']);
    const iElig = find(['gratuityeligible', 'eligible', 'accruesgratuity']);
    const iOpen = find(['openingbasicpaidthiscontract', 'openingbasicpaid', 'basicpaidtodate', 'openingbasic']);
    const iAnnEnt = find(['annualentitlement', 'annualdays']);
    const iAnnOpen = find(['annualopening', 'annualbalance', 'annualbroughtforward']);
    const iSickEnt = find(['sickentitlement', 'sickdays']);
    const iSickOpen = find(['sickopening', 'sickbalance', 'sickbroughtforward']);

    const rows: ParsedRow[] = [];
    const unmatched: string[] = [];
    const errors: string[] = [];
    const seen = new Set<string>();
    p.rows.forEach((r, li) => {
      const empRaw = cell(r, iEmp);
      if (!empRaw) { errors.push(`Row ${li + 2}: missing employee number.`); return; }
      const key = empRaw.trim().toLowerCase();
      const s = staffByEmp.get(key);
      if (!s) { unmatched.push(empRaw); return; }
      if (seen.has(key)) { errors.push(`Row ${li + 2}: duplicate employee ${empRaw} — using the first row.`); return; }
      seen.add(key);
      rows.push({
        staff: s,
        basic: hasValue(cell(r, iBasic)) ? numOr(cell(r, iBasic)) : undefined,
        gratuityRate: hasValue(cell(r, iRate)) ? numOr(cell(r, iRate)) : undefined,
        gratuityEligible: hasValue(cell(r, iElig)) ? boolOf(cell(r, iElig)) : undefined,
        openingBasic: hasValue(cell(r, iOpen)) ? numOr(cell(r, iOpen)) : undefined,
        annualEnt: hasValue(cell(r, iAnnEnt)) ? numOr(cell(r, iAnnEnt)) : undefined,
        annualOpen: hasValue(cell(r, iAnnOpen)) ? numOr(cell(r, iAnnOpen)) : undefined,
        sickEnt: hasValue(cell(r, iSickEnt)) ? numOr(cell(r, iSickEnt)) : undefined,
        sickOpen: hasValue(cell(r, iSickOpen)) ? numOr(cell(r, iSickOpen)) : undefined,
      });
    });
    setOutcome({ rows, unmatched, errors });
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    parse(await file.text());
    e.target.value = '';
  }

  async function commit() {
    if (!outcome || outcome.rows.length === 0) return;
    setBusy(true);
    let saved = 0;
    const year = String(new Date().getFullYear());
    try {
      for (const row of outcome.rows) {
        const sid = row.staff.id;
        // ---- payroll profile (preserve bank / allowances / band) ----
        const exP = profiles.find((p) => p.staffId === sid);
        const profile: Record<string, unknown> = {
          ...(exP ?? { staffId: sid, basicSalary: 0, allowances: [], gratuityRatePct: 0, gratuityEligible: true }),
          staffId: sid,
        };
        if (row.basic !== undefined) profile.basicSalary = round2(row.basic);
        if (row.gratuityRate !== undefined) profile.gratuityRatePct = round2(row.gratuityRate);
        if (row.gratuityEligible !== undefined) profile.gratuityEligible = row.gratuityEligible;
        if (row.openingBasic !== undefined) profile.openingBasicPaidThisContract = round2(row.openingBasic);
        await hr.payrollProfile.set(sid, profile);

        // ---- leave allocation (merge per-type) ----
        const exA = allocations.find((a) => a.staffId === sid);
        const entitlements: Record<string, number> = { ...(exA?.entitlements ?? {}) };
        const openingBalances: Record<string, number> = { ...(exA?.openingBalances ?? {}) };
        if (row.annualEnt !== undefined) entitlements.annual = row.annualEnt;
        if (row.sickEnt !== undefined) entitlements.sick = row.sickEnt;
        if (row.annualOpen !== undefined) openingBalances.annual = row.annualOpen;
        if (row.sickOpen !== undefined) openingBalances.sick = row.sickOpen;
        const touchedLeave =
          row.annualEnt !== undefined || row.sickEnt !== undefined ||
          row.annualOpen !== undefined || row.sickOpen !== undefined;
        if (touchedLeave || exA) {
          await hr.leaveAllocations.set(sid, {
            staffId: sid,
            year: exA?.year ?? year,
            entitlements,
            openingBalances,
          });
        }
        saved++;
      }
      await recordHrAudit({
        action: 'staff.migrate',
        actor,
        after: { staffMigrated: saved, year, fields: 'payrollProfile+leaveAllocations' },
      });
      setDone(saved);
    } finally {
      setBusy(false);
    }
  }

  const matched = outcome?.rows.length ?? 0;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <Users size={18} /> Import staff opening balances
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            One row per employee, matched on employee number. Sets basic salary, gratuity rate/eligibility, opening basic paid this contract, and opening leave (annual + sick). Blank cells are left unchanged. Other leave types can be set per-staff afterwards.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => downloadCsv('lkc_staff_opening_balances_template.csv', TEMPLATE)}
              className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Download size={15} /> Download template
            </button>
            <label className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl bg-[#1b3a6b] text-white hover:bg-[#2d5295] cursor-pointer">
              <Upload size={15} /> Choose CSV…
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
            </label>
          </div>

          {outcome && (
            <div className="space-y-3">
              {outcome.errors.length > 0 && (
                <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
                  {outcome.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}

              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-semibold">{matched} matched</span>
                {outcome.unmatched.length > 0 && (
                  <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-semibold">{outcome.unmatched.length} unmatched</span>
                )}
              </div>

              {outcome.unmatched.length > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  No staff found for: {outcome.unmatched.slice(0, 12).join(', ')}{outcome.unmatched.length > 12 ? '…' : ''}. Check the employee numbers.
                </p>
              )}

              {matched > 0 && (
                <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500 dark:text-gray-400 text-xs">
                      <tr>
                        <th className="text-left px-3 py-2">Employee</th>
                        <th className="text-right px-3 py-2">Basic</th>
                        <th className="text-right px-3 py-2">Grat %</th>
                        <th className="text-right px-3 py-2">Opening basic</th>
                        <th className="text-right px-3 py-2">Annual (ent/open)</th>
                        <th className="text-right px-3 py-2">Sick (ent/open)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outcome.rows.map((r) => (
                        <tr key={r.staff.id} className="border-t border-gray-100 dark:border-gray-700">
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-800 dark:text-gray-100">{staffFullName(r.staff)}</div>
                            <div className="text-xs text-gray-400">#{r.staff.employeeNo}</div>
                          </td>
                          <td className="px-3 py-2 text-right">{r.basic ?? '—'}</td>
                          <td className="px-3 py-2 text-right">{r.gratuityRate ?? '—'}{r.gratuityEligible === false ? ' (off)' : ''}</td>
                          <td className="px-3 py-2 text-right">{r.openingBasic ?? '—'}</td>
                          <td className="px-3 py-2 text-right">{r.annualEnt ?? '—'} / {r.annualOpen ?? '—'}</td>
                          <td className="px-3 py-2 text-right">{r.sickEnt ?? '—'} / {r.sickOpen ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {done != null && (
            <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
              <CheckCircle2 size={16} /> Imported opening balances for {done} staff.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700">
            {done != null ? 'Close' : 'Cancel'}
          </button>
          <AccentButton
            icon={busy ? Loader2 : CheckCircle2}
            disabled={busy || matched === 0 || done != null}
            onClick={commit}
          >
            {busy ? 'Importing…' : `Import ${matched || ''} staff`.trim()}
          </AccentButton>
        </div>
      </div>
    </div>
  );
}
